import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Power, 
  PowerOff, 
  Clock, 
  User, 
  Shield, 
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Lock,
  Zap
} from 'lucide-react';

// Category colors
const CATEGORY_COLORS = {
  system_critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-300',
  system: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  wallet: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  savings: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  investments: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  predictions: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  rewards: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  commerce: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  social: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
  pickup: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  advertising: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  agents: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  community: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
  vouchers: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  support: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200',
  auth: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200'
};

const CATEGORY_NAMES = {
  system_critical: '🔒 Critical System (Always On)',
  system: '⚙️ System',
  wallet: '💰 Wallet & Money',
  savings: '💎 Savings',
  investments: '📈 Investments',
  predictions: '🎯 Predictions',
  rewards: '🎁 Rewards',
  commerce: '🛒 Marketplace & Commerce',
  social: '🌐 Matrise Connect (Social)',
  pickup: '🚗 Matrise Pickup',
  advertising: '📢 Matrise Ads',
  agents: '👥 Agent Network',
  community: '🤝 Groups & Community',
  vouchers: '🎟️ Vouchers',
  support: '🆘 Support',
  auth: '📝 Authentication'
};

export const FeatureControlsPage = () => {
  const [features, setFeatures] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});
  const [bulkAction, setBulkAction] = useState(false);
  const [disableReasons, setDisableReasons] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const fetchFeatureControls = async () => {
    try {
      const response = await adminAPI.getFeatureControls();
      setFeatures(response.data.features || {});
      setHistory(response.data.history || []);
    } catch (error) {
      toast.error('Failed to load feature controls');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatureControls();
  }, []);

  const toggleFeature = async (featureId, currentlyEnabled) => {
    const newState = !currentlyEnabled;
    let reason = null;
    if (!newState && disableReasons[featureId]) {
      reason = disableReasons[featureId];
    }

    setToggling({ ...toggling, [featureId]: true });
    
    try {
      await adminAPI.toggleFeature(featureId, newState, reason);
      toast.success(`${features[featureId]?.name || 'Feature'} ${newState ? 'enabled' : 'disabled'}`);
      await fetchFeatureControls();
      setDisableReasons({ ...disableReasons, [featureId]: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle feature');
    } finally {
      setToggling({ ...toggling, [featureId]: false });
    }
  };

  const enableAllFeatures = async () => {
    if (!window.confirm('Enable all optional features?')) return;
    setBulkAction(true);
    try {
      await adminAPI.enableAllFeatures();
      toast.success('All optional features enabled');
      await fetchFeatureControls();
    } catch (error) {
      toast.error('Failed to enable all features');
    } finally {
      setBulkAction(false);
    }
  };

  const disableAllFeatures = async () => {
    const reason = window.prompt('Disable all optional features? Enter reason:');
    if (reason === null) return;
    
    setBulkAction(true);
    try {
      await adminAPI.disableAllFeatures(reason || 'Bulk disable by admin');
      toast.success('All optional features disabled');
      await fetchFeatureControls();
    } catch (error) {
      toast.error('Failed to disable all features');
    } finally {
      setBulkAction(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  // Group features by category
  const groupedFeatures = {};
  Object.entries(features).forEach(([featureId, feature]) => {
    const category = feature.category || 'other';
    if (!groupedFeatures[category]) {
      groupedFeatures[category] = [];
    }
    groupedFeatures[category].push({ id: featureId, ...feature });
  });

  // Filter features by search
  const filteredGrouped = {};
  Object.entries(groupedFeatures).forEach(([category, categoryFeatures]) => {
    const filtered = categoryFeatures.filter(f => 
      f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredGrouped[category] = filtered;
    }
  });

  // Count statistics
  const totalFeatures = Object.keys(features).length;
  const enabledCount = Object.values(features).filter(f => f.state?.enabled).length;
  const disabledCount = totalFeatures - enabledCount;
  const alwaysOnCount = Object.values(features).filter(f => f.always_on).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="feature-controls-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Feature Controls
          </h1>
          <p className="mt-2 text-muted-foreground">
            Granular control over every platform function. Changes take effect immediately.
          </p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{totalFeatures}</div>
                <div className="text-sm text-muted-foreground">Total Features</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{enabledCount}</div>
                <div className="text-sm text-muted-foreground">Enabled</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{disabledCount}</div>
                <div className="text-sm text-muted-foreground">Disabled</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">{alwaysOnCount}</div>
                <div className="text-sm text-muted-foreground">Protected</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={enableAllFeatures} 
              disabled={bulkAction}
              variant="outline" 
              className="gap-2"
            >
              <Zap className="w-4 h-4" />
              Enable All
            </Button>
            <Button 
              onClick={disableAllFeatures} 
              disabled={bulkAction}
              variant="outline" 
              className="gap-2"
            >
              <PowerOff className="w-4 h-4" />
              Disable All
            </Button>
            <Button onClick={fetchFeatureControls} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Feature Categories */}
        {Object.entries(filteredGrouped)
          .sort((a, b) => {
            // Sort: critical first, then alphabetically
            if (a[0] === 'system_critical') return -1;
            if (b[0] === 'system_critical') return 1;
            return a[0].localeCompare(b[0]);
          })
          .map(([category, categoryFeatures]) => (
            <Card key={category} className={category === 'system_critical' ? 'border-2 border-red-300' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {CATEGORY_NAMES[category] || category}
                  <Badge variant="outline">{categoryFeatures.length}</Badge>
                </CardTitle>
                {category === 'system_critical' && (
                  <CardDescription className="text-red-600 dark:text-red-400 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    These features cannot be disabled - they're required for system operation
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categoryFeatures.map((feature) => {
                    const isEnabled = feature.state?.enabled;
                    const isAlwaysOn = feature.always_on;

                    return (
                      <div
                        key={feature.id}
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{feature.name}</h3>
                            <Badge variant="outline" className={CATEGORY_COLORS[category]}>
                              {category.replace('_', ' ')}
                            </Badge>
                            {isAlwaysOn ? (
                              <Badge variant="secondary" className="gap-1">
                                <Lock className="w-3 h-3" />
                                Always On
                              </Badge>
                            ) : isEnabled ? (
                              <Badge variant="default" className="gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="gap-1">
                                <PowerOff className="w-3 h-3" />
                                Disabled
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{feature.description}</p>
                          
                          {!isEnabled && feature.state?.disabled_reason && (
                            <div className="p-2 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
                              <strong>Reason:</strong> {feature.state.disabled_reason}
                            </div>
                          )}

                          {!isEnabled && feature.state?.disabled_by && (
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <User className="w-3 h-3" />
                              Disabled by {feature.state.disabled_by}
                              {feature.state.disabled_at && (
                                <>
                                  <Clock className="w-3 h-3 ml-2" />
                                  {new Date(feature.state.disabled_at).toLocaleString()}
                                </>
                              )}
                            </div>
                          )}

                          {isEnabled && !isAlwaysOn && (
                            <div className="mt-2">
                              <Label htmlFor={`reason-${feature.id}`} className="text-xs text-muted-foreground">
                                Optional: Reason for disabling
                              </Label>
                              <Input
                                id={`reason-${feature.id}`}
                                placeholder="e.g., Maintenance, Testing, Emergency fix"
                                value={disableReasons[feature.id] || ''}
                                onChange={(e) => setDisableReasons({ ...disableReasons, [feature.id]: e.target.value })}
                                className="mt-1 text-sm"
                              />
                            </div>
                          )}
                        </div>

                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => toggleFeature(feature.id, isEnabled)}
                          disabled={toggling[feature.id] || isAlwaysOn}
                          className="ml-4 mt-1"
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

        {/* Change History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Change History
            </CardTitle>
            <CardDescription>Recent feature control changes (last 150)</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No changes recorded yet</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg text-sm">
                    <div className="p-1.5 bg-muted rounded">
                      {log.action === 'feature_control_bulk' ? (
                        <Zap className="w-4 h-4 text-orange-600" />
                      ) : log.metadata?.action === 'enabled' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <PowerOff className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      {log.action === 'feature_control_bulk' ? (
                        <p className="font-medium">
                          Bulk action:{' '}
                          <span className={log.metadata?.action === 'enable_all' ? 'text-green-600' : 'text-red-600'}>
                            {log.metadata?.action?.replace('_', ' ')}
                          </span>
                        </p>
                      ) : (
                        <p className="font-medium">
                          {log.metadata?.feature_name || log.metadata?.feature} was{' '}
                          <span className={log.metadata?.action === 'enabled' ? 'text-green-600' : 'text-red-600'}>
                            {log.metadata?.action}
                          </span>
                        </p>
                      )}
                      {log.metadata?.reason && (
                        <p className="text-muted-foreground">Reason: {log.metadata.reason}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {log.admin_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};